// constants.ts — Static mappings, types, and helpers shared across all views
// Extracted from App.tsx lines 12-82

// Empty string = same-origin relative paths (production). Fallback to localhost for dev.
const _env = process.env.REACT_APP_API_URL;
export const BASE = _env !== undefined && _env !== null ? _env : 'http://localhost:8000';
export const API = `${BASE}/api/v1/exceptions`;
export const API_V1 = `${BASE}/api/v1`;
export const FINANCIAL_API = `${BASE}/api/financial`;

/** Authenticated fetch — injects Bearer token + auto-refreshes on 401 */
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('agentcfo_access_token');
  if (token) {
    const headers = new Headers(options.headers);
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    options = { ...options, headers };
  }
  const res = await fetch(url, options);
  if (res.status === 401) {
    // Try to refresh the token
    const refreshToken = localStorage.getItem('agentcfo_refresh_token');
    if (refreshToken) {
      try {
        const refreshRes = await fetch(`${API_V1}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          localStorage.setItem('agentcfo_access_token', data.access_token);
          if (data.refresh_token) localStorage.setItem('agentcfo_refresh_token', data.refresh_token);
          // Retry the original request with the new token
          const retryHeaders = new Headers(options.headers);
          retryHeaders.set('Authorization', `Bearer ${data.access_token}`);
          return fetch(url, { ...options, headers: retryHeaders });
        }
      } catch { /* refresh failed — return original 401 */ }
    }
  }
  return res;
};

/** Fetch with graceful 404 handling — returns fallback on non-OK responses instead of throwing */
export const safeFetch = (url: string, fallback: any = {}) =>
  authFetch(url).then(r => r.ok ? r.json() : fallback).catch(() => fallback);

// ── DOA categories — multi-entity DOA Phase 3 ──────────────────
// Single source of truth shared by every approval button (Phase 3) and the
// Client Policies matrix UI (Phase 4). Each code maps to a doa_category
// string the backend services use when spawning approval actions and to a
// row in org_configuration as `DOA_{CODE}_{LEVEL}`. Admins can add custom
// categories at runtime via Client Policies — the codes here are just the
// well-known defaults the platform's services tag actions with.
export interface DOACategory {
  code: string;
  label: string;
  description: string;
}

export const DOA_CATEGORIES: readonly DOACategory[] = [
  { code: 'CONTRACT',       label: 'Contract Commitment', description: 'Long-term contracts and service agreements' },
  { code: 'CAPEX',          label: 'Capital Expenditure', description: 'Fixed asset purchases and capex projects' },
  { code: 'JV',             label: 'Journal Entry',       description: 'Manual JEs, accruals, reclassifications' },
  { code: 'VENDOR_PAYMENT', label: 'Vendor Payment',      description: 'Payment runs, wire transfers, disbursements' },
  { code: 'WRITE_OFF',      label: 'Write-Off',           description: 'Bad debt, inventory obsolescence, AR write-off' },
  { code: 'EXPENSE',        label: 'Expense Report',      description: 'Employee expense reimbursements' },
  { code: 'PO',             label: 'Purchase Order',      description: 'PO approval' },
  { code: 'AP_INVOICE',     label: 'AP Invoice',          description: 'Accounts payable invoice approval' },
  { code: 'PR',             label: 'Purchase Requisition',description: 'PR approval before PO' },
  { code: 'SO',             label: 'Sales Order',         description: 'SO confirmation and credit review' },
  { code: 'CUSTOMER',       label: 'Customer Onboarding', description: 'New customer credit limit approval' },
  { code: 'VENDOR',         label: 'Vendor Onboarding',   description: 'New vendor activation (L3 minimum)' },
  { code: 'CREDIT_MEMO',    label: 'Credit/Debit Memo',   description: 'Customer credit memos and debit notes' },
  { code: 'BUDGET',         label: 'Budget',              description: 'Budget header approval (activation)' },
] as const;

/** Lookup map for category labels (e.g. for tooltips). */
export const DOA_CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  DOA_CATEGORIES.map(c => [c.code, c.label])
);

// Department config — maps agent IDs to departments with colors and URL slugs
export const DEPARTMENTS: Record<string, { color: string; slug: string; agents: string[] }> = {
  'Finance Operations': { color: '#3b82f6', slug: 'finance-operations', agents: ['ap_accountant', 'ar_specialist', 'ap_supervisor', 'ar_supervisor', 'vp_finance'] },
  'Accounting':         { color: '#8b5cf6', slug: 'accounting', agents: ['staff_accountant', 'senior_accountant', 'controller_support'] },
  'FP&A':              { color: '#06b6d4', slug: 'fpa', agents: ['fpa_analyst', 'fpa_senior', 'fpa_director'] },
  'Tax':               { color: '#f59e0b', slug: 'tax', agents: ['tax_analyst', 'tax_senior', 'tax_compliance'] },
  'Treasury':          { color: '#10b981', slug: 'treasury', agents: ['cash_analyst', 'treasury_senior', 'treasury_analyst'] },
  'Payroll':           { color: '#ec4899', slug: 'payroll', agents: ['payroll_processor', 'payroll_compliance', 'payroll_manager'] },
  'Procurement':       { color: '#f97316', slug: 'procurement', agents: ['procurement_buyer', 'procurement_supervisor', 'contract_analyst', 'vendor_manager'] },
  'Internal Audit':    { color: '#ef4444', slug: 'internal-audit', agents: ['controls_tester', 'audit_analyst', 'sox_compliance'] },
  'Credit & Risk':     { color: '#14b8a6', slug: 'credit-risk', agents: ['credit_analyst', 'credit_senior', 'risk_analyst'] },
  'Financial Systems': { color: '#a855f7', slug: 'financial-systems', agents: ['data_quality_analyst', 'integration_specialist', 'systems_manager'] },
  'CFO Office':        { color: '#6F42C1', slug: 'cfo-office', agents: ['chief_financial_officer'] },
};

// Agent-to-escalation-level mapping
export const AGENT_ESCALATION: Record<string, { level: string; limit: number }> = {
  // L2 Analyst ($50K)
  ap_accountant: { level: 'L2', limit: 50000 }, ar_specialist: { level: 'L2', limit: 50000 },
  staff_accountant: { level: 'L2', limit: 50000 }, payroll_processor: { level: 'L2', limit: 50000 },
  controls_tester: { level: 'L2', limit: 50000 }, fpa_analyst: { level: 'L2', limit: 50000 },
  procurement_buyer: { level: 'L2', limit: 50000 }, vendor_manager: { level: 'L2', limit: 50000 },
  credit_analyst: { level: 'L2', limit: 50000 }, data_quality_analyst: { level: 'L2', limit: 50000 },
  integration_specialist: { level: 'L2', limit: 50000 },
  tax_analyst: { level: 'L2', limit: 50000 }, cash_analyst: { level: 'L2', limit: 50000 },
  // L3 Supervisor ($250K)
  ap_supervisor: { level: 'L3', limit: 250000 }, ar_supervisor: { level: 'L3', limit: 250000 },
  audit_analyst: { level: 'L3', limit: 250000 }, payroll_compliance: { level: 'L3', limit: 250000 },
  tax_senior: { level: 'L3', limit: 250000 }, treasury_senior: { level: 'L3', limit: 250000 },
  senior_accountant: { level: 'L3', limit: 250000 }, fpa_senior: { level: 'L3', limit: 250000 },
  credit_senior: { level: 'L3', limit: 250000 }, procurement_supervisor: { level: 'L3', limit: 250000 },
  // L4 Manager ($1M)
  controller_support: { level: 'L4', limit: 1000000 }, treasury_analyst: { level: 'L4', limit: 1000000 },
  contract_analyst: { level: 'L4', limit: 1000000 }, tax_compliance: { level: 'L4', limit: 1000000 },
  payroll_manager: { level: 'L4', limit: 1000000 }, systems_manager: { level: 'L4', limit: 1000000 },
  // L5 Director ($5M)
  vp_finance: { level: 'L5', limit: 5000000 }, fpa_director: { level: 'L5', limit: 5000000 },
  risk_analyst: { level: 'L5', limit: 5000000 }, sox_compliance: { level: 'L5', limit: 5000000 },
  // L6 CFO (Unlimited)
  chief_financial_officer: { level: 'L6', limit: 50000000 },
};

// Level band limits — each level only sees tasks within its amount band
export const LEVEL_BANDS: Record<string, { floor: number; ceiling: number }> = {
  L2: { floor: 0, ceiling: 50000 },
  L3: { floor: 50000, ceiling: 250000 },
  L4: { floor: 250000, ceiling: 1000000 },
  L5: { floor: 1000000, ceiling: 5000000 },
  L6: { floor: 5000000, ceiling: 50000000 },
};

// Escalation level display config
export const LEVEL_CONFIG: Record<string, { name: string; color: string; bg: string }> = {
  L6: { name: 'CFO', color: '#6F42C1', bg: '#4C1D95' },
  L5: { name: 'Director', color: '#DC3545', bg: '#7F1D1D' },
  L4: { name: 'Manager', color: '#FD7E14', bg: '#7C2D12' },
  L3: { name: 'Supervisor', color: '#FFC107', bg: '#78350F' },
  L2: { name: 'Analyst', color: '#17A2B8', bg: '#0C4A6E' },
};

// Agent-to-DB-department mapping for functional routing within shared departments.
// Only needed where a display department has multiple functional areas (e.g. Finance Operations has AP + AR).
// Agents NOT in this map handle ALL DB departments in their display department.
export const AGENT_FUNCTION_DEPTS: Record<string, string[]> = {
  ap_accountant: ['accounts_payable'],
  ap_supervisor: ['accounts_payable'],
  ar_specialist: ['accounts_receivable'],
  ar_supervisor: ['accounts_receivable'],
  vp_finance: ['accounts_payable', 'accounts_receivable'],  // L5 handles both
};

// Map exception department names (from DB) to display department names
export const DEPT_DB_MAP: Record<string, string> = {
  'accounts_payable': 'Finance Operations',
  'accounts_receivable': 'Finance Operations',
  'financial_reporting': 'Accounting',
  'internal_controls': 'Internal Audit',
  'tax_compliance': 'Tax',
  'treasury': 'Treasury',
  'payroll': 'Payroll',
  'procurement': 'Procurement',
  'intercompany': 'Accounting',
  'banking': 'Treasury',
  'general_ledger': 'Accounting',
  'fpa': 'FP&A',
  'credit_risk': 'Credit & Risk',
  'financial_systems': 'Financial Systems',
  'cfo_office': 'CFO Office',
  'systems': 'Financial Systems',
  'subledger_ap': 'Finance Operations',
  'subledger_ar': 'Finance Operations',
  'subledger_fa': 'Accounting',
  'subledger_inv': 'Accounting',
  'credit_management': 'Credit & Risk',
  'financial_planning': 'FP&A',
  'custom': 'CFO Office',
};

// Reverse map: display dept -> DB dept names
export const DEPT_REVERSE_MAP: Record<string, string[]> = {};
for (const [dbName, displayName] of Object.entries(DEPT_DB_MAP)) {
  if (!DEPT_REVERSE_MAP[displayName]) DEPT_REVERSE_MAP[displayName] = [];
  DEPT_REVERSE_MAP[displayName].push(dbName);
}

// Slug lookup maps
const SLUG_TO_DEPT: Record<string, string> = {};
const DEPT_TO_SLUG: Record<string, string> = {};
for (const [name, cfg] of Object.entries(DEPARTMENTS)) {
  SLUG_TO_DEPT[cfg.slug] = name;
  DEPT_TO_SLUG[name] = cfg.slug;
}

/** Convert snake_case or lowercase text to Title Case display */
export function titleCase(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function slugify(deptName: string): string {
  return DEPT_TO_SLUG[deptName] || deptName.toLowerCase().replace(/[&\s]+/g, '-');
}

export function deslugify(slug: string): string {
  return SLUG_TO_DEPT[slug] || slug;
}

// Role definitions for simulator
export type Role = {
  id: string;
  label: string;
  level: string;
  departments: string[] | 'all';
};

export const ROLES: Role[] = [
  { id: 'cfo', label: 'CFO', level: 'L6', departments: 'all' },
  { id: 'vp_finance', label: 'VP Finance', level: 'L5', departments: ['Finance Operations'] },
  { id: 'controller', label: 'Controller', level: 'L4', departments: ['Accounting'] },
  { id: 'treasury_mgr', label: 'Treasury Manager', level: 'L4', departments: ['Treasury'] },
  { id: 'senior_accountant', label: 'Senior Accountant', level: 'L3', departments: ['Accounting'] },
  { id: 'ap_supervisor', label: 'AP Supervisor', level: 'L3', departments: ['Finance Operations'] },
  { id: 'analyst', label: 'Analyst', level: 'L2', departments: ['Finance Operations'] },
];

// Health score formula
export function calculateHealthScore(stats: { exceptions: number; resolved: number; escalated: number; flagged: number; autoResolved?: number }): number {
  const { exceptions, resolved, escalated, flagged } = stats;
  const autoResolved = stats.autoResolved || 0;
  if (exceptions === 0) return 100;
  const resolutionRate = (resolved + autoResolved) / Math.max(exceptions, 1);
  const escalationPenalty = escalated / Math.max(exceptions, 1);
  const flaggedPenalty = flagged / Math.max(exceptions, 1);
  const score = (resolutionRate * 50) + ((1 - escalationPenalty) * 30) + ((1 - flaggedPenalty) * 20);
  return Math.round(Math.max(0, Math.min(100, score)));
}


// Format amount helper — exact, to the cent. Default for transaction
// tables, line items, JE postings, anything where a finance user
// would expect to reconcile to source documents to the penny.
// Per Albert's 2026-05-21 feedback: "this is a finance system,
// transactions have to be reflected at the cent level for every
// dashboard." The prior behavior dropped cents AND used K/M/B
// abbreviation across all 678 call sites -- silent auditor risk.
export function formatAmount(amt: number | null | undefined): string {
  if (amt === null || amt === undefined || !isFinite(amt as number)) return 'Unlimited';
  // Negative numbers render as -$1,234.56 not $-1,234.56 for accountant-conventional readability.
  const n = amt as number;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Compact format -- K / M / B abbreviation. ONLY appropriate for KPI
// summary cards where the rounded headline is the point. NEVER use
// for transaction-level displays (tables, line items, JE distributions).
export function formatAmountCompact(amt: number | null | undefined): string {
  if (amt === null || amt === undefined || !isFinite(amt as number)) return 'Unlimited';
  const n = amt as number;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

// Level number comparison
const LEVEL_ORDER: Record<string, number> = { L1: 1, L2: 2, L3: 3, L4: 4, L5: 5, L6: 6, L7: 7 };
export function levelNum(level: string): number {
  return LEVEL_ORDER[level] || 0;
}
